/**
 * AdminBAA — HIPAA Business Associate Agreement signing surface.
 *
 * Shows the canonical BAA text, requires admin to type their full name as
 * signature (must match), title, and accept terms. On submit:
 *   POST /api/baa/sign  → returns BAAStatus.
 *
 * Once signed, shows a download button for the signed PDF.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, FileDown, Loader2, Sparkles, AlertCircle } from "lucide-react";

export default function AdminBAA() {
  const [status, setStatus] = useState(null);
  const [baaText, setBaaText] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ full_name: "", title: "", signature_text: "", agree: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/baa/me"),
      api.get("/legal/baa-text"),
    ])
      .then(([s, b]) => {
        if (!alive) return;
        setStatus(s.data);
        setBaaText(b.data.content_markdown);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
  }, []);

  const sign = async (e) => {
    e.preventDefault();
    if (form.signature_text.trim().toLowerCase() !== form.full_name.trim().toLowerCase()) {
      toast.error("Signature must match your full name exactly");
      return;
    }
    if (!form.agree) {
      toast.error("Please tick the box to confirm you accept the agreement");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/baa/sign", form);
      setStatus(res.data);
      toast.success("BAA signed. A PDF copy is now available.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign BAA");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/baa/${status.record_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `afyanex-baa-${(status.signed_at || "").slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  if (loading) {
    return (
      <div className="card-surface p-10 flex items-center justify-center text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading agreement…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-baa-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Compliance</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">
            HIPAA Business Associate Agreement
          </h1>
          <p className="mt-1 text-sm text-clinical-subtext max-w-2xl">
            Before your agency processes any patient PHI through Afyanex, an
            authorised representative must sign this Business Associate
            Agreement. A countersigned PDF copy is generated for your records.
          </p>
        </div>
      </div>

      {status?.is_signed ? (
        <div className="card-surface p-6" data-testid="baa-signed-state">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <div className="font-heading text-base font-semibold text-clinical-text">
                Your BAA is signed and on file
              </div>
              <div className="mt-1 text-sm text-clinical-subtext">
                Signed by <strong className="text-clinical-text">{status.signed_by_name}</strong>
                {status.signed_by_title && <>, {status.signed_by_title}</>} on{" "}
                {new Date(status.signed_at).toLocaleString()}.
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={downloadPdf}
                  className="btn-primary"
                  data-testid="baa-download-pdf"
                >
                  <FileDown className="h-4 w-4" /> Download signed PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-4"
             data-testid="baa-pending-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <div className="font-heading text-sm font-semibold text-amber-900">
                Action required: BAA not yet signed
              </div>
              <p className="text-sm text-amber-900/80 mt-1">
                Until this is signed, please do not store real patient
                identifiers in production. Sample / test data is fine.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card-surface p-6 prose prose-sm max-w-none">
        <div className="space-y-2 text-sm leading-relaxed text-clinical-text"
             data-testid="baa-text">
          {baaText.split("\n\n").map((block, i) => {
            const trimmed = block.trim();
            if (trimmed.startsWith("# ")) {
              return <h1 key={i} className="font-heading text-2xl font-semibold mt-0 mb-2">{trimmed.slice(2)}</h1>;
            }
            if (trimmed.startsWith("## ")) {
              return <h2 key={i} className="font-heading text-base font-semibold mt-5 mb-1">{trimmed.slice(3)}</h2>;
            }
            if (trimmed.startsWith("- ")) {
              const items = trimmed.split("\n").map((l) => l.replace(/^- /, "").trim());
              return (
                <ul key={i} className="list-disc pl-5 space-y-1">
                  {items.map((it, j) => (<li key={j}>{it}</li>))}
                </ul>
              );
            }
            return <p key={i}>{trimmed}</p>;
          })}
        </div>
      </div>

      {!status?.is_signed && (
        <form onSubmit={sign} className="card-surface p-6 space-y-4" data-testid="baa-sign-form">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-clinical-primary" />
            <h2 className="font-heading text-base font-semibold">Sign on behalf of your agency</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow mb-1 block">Your full name</label>
              <input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
                className="input-clinical"
                placeholder="Alex Morgan"
                data-testid="baa-full-name"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1 block">Your title</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                className="input-clinical"
                placeholder="Agency Director"
                data-testid="baa-title"
              />
            </div>
          </div>
          <div>
            <label className="label-eyebrow mb-1 block">Type your name as signature</label>
            <input
              value={form.signature_text}
              onChange={(e) => setForm({ ...form, signature_text: e.target.value })}
              required
              className="input-clinical font-heading italic text-lg"
              style={{ fontFamily: "'Caveat', cursive, system-ui" }}
              placeholder="Type the same name to sign"
              data-testid="baa-signature"
            />
            <p className="mt-1 text-[11px] text-clinical-subtext">
              Must match your full name above (case-insensitive).
            </p>
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.agree}
              onChange={(e) => setForm({ ...form, agree: e.target.checked })}
              className="mt-1"
              data-testid="baa-agree-checkbox"
            />
            <span className="text-sm text-clinical-text">
              I have read this agreement, I am authorised to bind my agency to
              its terms, and I accept it on behalf of my agency.
            </span>
          </label>
          <div className="pt-2 border-t border-clinical-border flex items-center justify-end">
            <button
              type="submit"
              disabled={saving || !form.agree}
              className="btn-primary disabled:opacity-50"
              data-testid="baa-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Sign BAA
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
